import { ButtonLink, DataTable, Icon, Image, Modal, Text, Tooltip } from "components/commons";
import React, { useContext, useState } from "react";
import getColumns from "./columns";
import lang from "translations";
import { useMount } from "hooks";
import { formatDate } from "services";
import { formatNumberToMoneyWithCurrencyParams } from "services/money.service";
import { DateTime } from "enums";
import { mixpanel } from "mixpanel";
import { VenueContext } from "contexts";
import { isEatappVenue } from "services/venue.service";

const BookingGuests = ({ data: bookingDetailsData, loading: loadingBookingDetails, error }) => {
  const { venue } = useContext(VenueContext);
  const [idScanPreview, setIdScanPreview] = useState(null);

  useMount(() => {
    mixpanel.track(TrackEvent.VisitedPage, {
      Page: lang.bookingGuests,
    });
  });

  const isEatapp = isEatappVenue(venue);
  const guests = bookingDetailsData?.customers ?? [];
  // On an EatApp reservation the endpoint returns a row per seat, named `Guest N` by the BE while
  // its guest has not filled in the pre-checkin form. Pad up to the reservation guest
  // count anyway, so a response that is short of seats still lists every one of them.
  // Every other venue lists exactly the guests it was given, as it did before.
  const totalGuests = isEatapp
    ? Math.max(bookingDetailsData?.reservationGuestCount ?? 0, guests.length)
    : guests.length;

  const renderIdScan = (idScan, guestName) => {
    if (!idScan) {
      return null;
    }
    return (
      <ButtonLink
        className="whitespace-nowrap"
        onClick={() => setIdScanPreview({ src: idScan, name: guestName })}
      >
        {lang.view}
      </ButtonLink>
    );
  };

  const renderPlaceholderRow = (index) => {
    return {
      name: (
        <div className="flex items-center mt-sm mb-sm">
          <Text color="text-gray">{lang.populate(lang.guestNumber, [index + 1])}</Text>
        </div>
      ),
    };
  };

  const renderGuestRow = (customer, index) => {
    const {
      name,
      checkedInAt,
      isOwner,
      email,
      mobileNumber,
      gender,
      genderRaw,
      birthdate,
      nationality,
      identificationType,
      idNumber,
      idScan,
      medicalCondition,
      foodAllergies,
      specialNotes,
      freeCredit,
      guestProfile,
      ticketNo,
      preloadedCredit,
      staffNote,
    } = customer;
    const displayName = name || lang.populate(lang.guestNumber, [index + 1]);
    return {
      name: (
        <div className="flex items-center mt-sm mb-sm">
          <Text>{displayName}</Text>
          {isOwner && (
            <Tooltip
              title={isEatapp ? lang.headOfReservation : lang.headOfBooking}
              placement="top"
            >
              <div className="p-0 m-0 h-min flex items-start -mt-1">
                <Icon name="star" color="text-pelorous" className="text-xxs" />
              </div>
            </Tooltip>
          )}
        </div>
      ),
      // Soar pre-checkin submits a title (Mr/Ms/Mrs) in the gender field, which
      // gender2str leaves unmapped.
      gender: <Text>{gender || genderRaw}</Text>,
      email: <Text>{email}</Text>,
      mobileNumber: <Text>{mobileNumber}</Text>,
      birthdate: <Text>{formatDate(birthdate, DateTime.V)}</Text>,
      nationality: <Text>{nationality}</Text>,
      identificationType: <Text>{identificationType}</Text>,
      idNumber: <Text>{idNumber}</Text>,
      idScan: renderIdScan(idScan, displayName),
      medicalCondition: <Text>{medicalCondition}</Text>,
      foodAllergies: <Text>{foodAllergies}</Text>,
      // The pre-checkin form collects this one in a textarea, so the cell keeps its line
      // breaks. Medical Condition and Food Allergies are single-line inputs there.
      specialNotes: <Text className="whitespace-pre-wrap">{specialNotes}</Text>,
      freeCredit: (
        <Text>
          {freeCredit === null || freeCredit === undefined
            ? ""
            : formatNumberToMoneyWithCurrencyParams(freeCredit, true, venue.currencyCode)}
        </Text>
      ),
      guestProfile: <Text>{guestProfile}</Text>,
      ticketNo: <Text>{ticketNo}</Text>,
      preloadedCredit: <Text>{preloadedCredit}</Text>,
      staffNote: <Text className="whitespace-pre-wrap">{staffNote}</Text>,
      checkinDate: checkedInAt ? (
        formatDate(checkedInAt, DateTime.I)
      ) : (
        <Text color="text-gray">{lang.notCheckedIn}</Text>
      ),
    };
  };

  const renderData = () => {
    return Array.from({ length: totalGuests }, (_, index) => {
      const customer = guests[index];
      return customer ? renderGuestRow(customer, index) : renderPlaceholderRow(index);
    });
  };

  return (
    <>
      <DataTable
        loading={loadingBookingDetails}
        error={error}
        total={totalGuests}
        title={lang.guestList}
        columns={getColumns(isEatapp)}
        data={renderData()}
        minWidth={isEatapp ? "2000px" : "800px"}
      />
      <Modal
        active={Boolean(idScanPreview)}
        title={lang.idScan}
        close={() => setIdScanPreview(null)}
        width={640}
      >
        {idScanPreview && (
          <Image src={idScanPreview.src} alt={idScanPreview.name} className="w-full" />
        )}
      </Modal>
    </>
  );
};

export default BookingGuests;
